import { reportErrorToConsole } from '@src/services/error-logger'
import { apiClient } from '@src/services/http-common'
import { FailedVendorSyncItem, RetryFailedVendorResult } from '@src/types/failed-vendor-sync'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const SCOPE = 'failed-vendors'

export const failedVendorsQueryKeys = {
  all: [SCOPE] as const,
  byCompany: (companyId: string) => [...failedVendorsQueryKeys.all, companyId] as const,
}

const getFailedVendors = async (companyId: string): Promise<FailedVendorSyncItem[]> => {
  try {
    const response = await apiClient.get<FailedVendorSyncItem[]>('accounting/failed-vendors', {
      params: { companyId },
    })
    return response.data ?? []
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

const retryFailedVendor = async (merchantId: string, companyId: string): Promise<RetryFailedVendorResult> => {
  const response = await apiClient.post<RetryFailedVendorResult>(
    `accounting/failed-vendors/${encodeURIComponent(merchantId)}/retry`,
    null,
    { params: { companyId } }
  )
  return response.data
}

export const useGetFailedVendors = (companyId: string) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: failedVendorsQueryKeys.byCompany(companyId),
    queryFn: () => getFailedVendors(companyId),
    enabled: !!companyId,
  })

  return { data, isLoading, error, refetch }
}

export const useRetryFailedVendor = (companyId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (merchantId: string) => retryFailedVendor(merchantId, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: failedVendorsQueryKeys.byCompany(companyId) })
    },
    onError: (error) => {
      reportErrorToConsole(error)
    },
  })
}
